/*
  Tyee Marine - the Canadian counter-tariff surcharge, kept right in the cart (port of tyeearchery.ca's v2.1.1
  reconciler, 16 Oct 2026; out/reports/tariff-2026-10-16/ has the spec, the archery source and the tests).

  WHAT IT DOES. For every cart line whose product carries the tag tariff-rate-50 or tariff-rate-25, the units beyond
  what Campbell River has on the shelf carry a surcharge of that rate on the item's price. The surcharge is its own
  cart line (the product tagged tyee-surcharge), one line per item, carrying _tariff_for = the item's variant id and a
  visible "Applied to: <item>". Lines are added, re-sized and removed so the cart always matches; before checkout the
  cart is checked once more, and checkout waits unless the cart the customer is sent with is the cart that was checked.

  WHAT IS DIFFERENT FROM ARCHERY, ON PURPOSE:
    - nothing is charged unless a product is TAGGED: a missing or 0 rate is 0 (archery read 0 as 50);
    - nothing runs unless the config says "enabled": true (archery ran unless it said false);
    - a failed lookup (the product, its tags, its shelf count, or an option missing from the count) charges NOTHING
      for that item, leaves its surcharge lines alone, and holds checkout with a message (archery charged in full);
    - no Tariff-Free Zone; express-checkout buttons are hidden by tyee-tariff.css, not removed here;
    - the product page's panel is redrawn from the page the variant picker fetches (Horizon's own event).
  And after the two reviews of 16 Oct 2026 (theme/review_findings.json, review2_findings.json):
    - CHECKOUT: after the check the browser goes to /checkout by itself - the cart form's quantity boxes are never
      posted - and only if a fresh read of the cart is exactly the cart the check settled on, and the boxes on screen
      say the same. A press before this script has started, or in a browser that does not say which button was
      pressed (Safari before 15.4), is caught too (snippets/tyee-tariff.liquid holds it until the script is ready);
    - shelf counts are kept 60 s from when they were read, and read fresh (tagged products only, four at a time)
      for the check before checkout; one read per product per check, so a failure is never forgotten in a later pass;
    - a cart that does not come right in four passes holds checkout (after one last look at it);
    - switched off, surcharge lines left in a cart are taken out;
    - the cart's own controls are locked while surcharge lines move, and the counts corrected after Horizon redraws;
      only exactly tariff-rate-50 / tariff-rate-25 count, as on the product page.
  The config is snippets/tyee-tariff.liquid's <script id="tyee-tariff-config">, from the shop metafield
  tyee.tariff_map: { enabled, rate, product_id, handle, tag_fee, amounts: { "<cents>": <variant id> } }.
  Debug: add ?tyee_debug=1 to an address, or localStorage tyee_tariff_debug = 1.
*/
import { CartLinesUpdateEvent } from 'https://cdn.shopify.com/storefront/standard-events.js';

const VERSION = 'tm-1.1.0';
const PROP_FOR = '_tariff_for';
const PROP_RATE = '_tariff_rate';
const PROP_PART = '_tariff_part';
const PROP_LABEL = 'Applied to';
const LADDER = [100000, 10000, 2000, 500, 100, 25, 5, 1];
const CART_URL = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
const MARK = 'X-Tyee-Tariff';
const TTL = 60 * 1000;                                       // a shelf count is good for 60 s from when it was read
const CHECK_CAP = 10000;                                     // the check before checkout gets 10 s
const POOL = 4;                                              // product reads at a time

const DEBUG = /[?&]tyee_debug=1/.test(location.search) || safeLocal('tyee_tariff_debug') === '1';
const log = (...a) => { if (DEBUG) console.log('[tyee-tariff]', ...a); };
const warn = (...a) => console.warn('[tyee-tariff]', ...a);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function safeLocal(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}

/* ---- the config ------------------------------------------------------------------------------------------------ */
const CFG = (() => {
  const el = document.getElementById('tyee-tariff-config');
  if (!el) return null;
  let c;
  try {
    c = JSON.parse(el.textContent || '{}');
    if (typeof c === 'string') c = JSON.parse(c);           // a metafield stored as text rather than JSON
  } catch (e) {
    warn('config unreadable - the surcharge is OFF', e);
    return null;
  }
  if (!c || typeof c !== 'object') return null;
  const rate = Number(c.rate);
  const cfg = {
    enabled: c.enabled === true,
    product_id: Number(c.product_id) || 0,
    handle: typeof c.handle === 'string' ? c.handle : '',
    rate: Number.isFinite(rate) && rate > 0 && rate <= 100 ? rate : 0,   // read for the record; never charged (rateFromTags)
    tag_fee: String(c.tag_fee || 'tyee-surcharge'),
    amounts: c.amounts && typeof c.amounts === 'object' ? c.amounts : {},
    currency: el.dataset.currency || 'CAD',
  };
  if (cfg.enabled && (!cfg.product_id || !Object.keys(cfg.amounts).length)) {
    warn('config has no surcharge product or amounts - the surcharge is OFF');
    cfg.enabled = false;
  }
  return cfg;
})();

const state = { running: null, queued: false, scheduled: false, failed: [], unfixable: [], settledCart: null, countCart: null, lastOkAt: 0, busy: false };
window.TyeeTariff = { version: VERSION, config: CFG, state, reconcile: (why) => reconcile(why === 'checkout' ? 'checkout' : 'manual') };
if (!CFG || !CFG.enabled) log('the surcharge is off (config ' + (CFG ? 'enabled:false' : 'missing') + ')');

/* ---- reading the store ----------------------------------------------------------------------------------------- */
async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...opts,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', [MARK]: '1', ...(opts.headers || {}) },
  });
  const text = await res.text();
  let json = null;
  try { json = JSON.parse(text); } catch (e) { /* not JSON */ }
  if (!res.ok) {
    const err = new Error(url + ' -> HTTP ' + res.status);
    err.body = json || text;
    throw err;
  }
  return json;
}
const readCart = () => fetchJSON('/cart.js', { cache: 'no-store' });

const skey = (handle) => 'tyee_tariff_tm1:p:' + handle;
function ssRead(handle) {                                    // the tab's copy, only while it is under 60 s old
  try {
    const o = JSON.parse(sessionStorage.getItem(skey(handle)) || 'null');
    return o && o.readAt && Date.now() - o.readAt < TTL ? o : null;
  } catch (e) { return null; }
}
function ssWrite(handle, info) {                             // never over a NEWER copy (a slow read landing late)
  try {
    const prev = JSON.parse(sessionStorage.getItem(skey(handle)) || 'null');
    if (!prev || !prev.readAt || prev.readAt <= info.readAt) sessionStorage.setItem(skey(handle), JSON.stringify(info));
  } catch (e) { /* full or blocked */ }
}

/* null = the surcharge product itself; 50 or 25 for exactly the tags tariff-rate-50 / tariff-rate-25 (50 first),
   read the same way as the product page and the cards read them (Liquid's exact `contains`); anything else is 0.
   An UNTAGGED product is 0, always: the config's "rate" is never used (the Go Live session, 16 Oct 2026: "Ours must
   NEVER charge an untagged product"), so a slip in the metafield cannot put a surcharge on the whole store. */
function rateFromTags(tags) {
  if (!Array.isArray(tags)) return 0;
  if (tags.includes(CFG ? CFG.tag_fee : 'tyee-surcharge')) return null;
  if (tags.includes('tariff-rate-50')) return 50;
  if (tags.includes('tariff-rate-25')) return 25;
  return 0;
}

async function lookUp(handle) {
  const readAt = Date.now();
  const j = await fetchJSON('/products/' + encodeURIComponent(handle) + '.js', { cache: 'no-store' });
  if (!j || !Array.isArray(j.tags)) throw new Error('no tags in the product data');
  const tags = j.tags.map((t) => String(t).trim());         // exact, as Liquid's `contains` reads them
  let onhand = null;
  if (rateFromTags(tags) > 0) {                              // the shelf count matters only for a tagged product
    const res = await fetch('/products/' + encodeURIComponent(handle) + '?view=tyee-stock', {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html', [MARK]: '1' },
    });
    if (!res.ok) throw new Error('stock view HTTP ' + res.status);
    const s = JSON.parse(await res.text());
    if (!s || typeof s.onhand !== 'object') throw new Error('stock view has no onhand');
    onhand = {};
    for (const [vid, q] of Object.entries(s.onhand)) onhand[String(vid)] = Math.max(0, Number(q) || 0);
  }
  const info = { tags, title: j.title, onhand, readAt };
  ssWrite(handle, info);
  return info;
}

/* A product's tags and shelf counts: {failed: true} when any part could not be read. Kept 60 s from the moment it was
   read (in this page and in the tab's sessionStorage). fresh = the check before checkout: a TAGGED product (or one
   not known yet) is read again; an untagged one's tags, under 60 s old, will do. A failure is never kept. */
const infoCache = new Map();                                 // handle -> { at, p, tagged }
function productInfo(handle, fresh) {
  const hit = infoCache.get(handle);
  if (hit && Date.now() - hit.at < TTL && !(fresh && hit.tagged !== false)) return hit.p;
  const s = ssRead(handle);
  if (s && !(fresh && rateFromTags(s.tags) > 0)) {
    const p = Promise.resolve(s);
    infoCache.set(handle, { at: s.readAt, p, tagged: rateFromTags(s.tags) > 0 });
    return p;
  }
  const p = lookUp(handle).catch((e) => {
    warn('lookup failed - nothing charged for it, its lines left alone, checkout held', handle, e.message);
    return { failed: true, why: e.message };
  });
  const entry = { at: Date.now(), p, tagged: undefined };
  infoCache.set(handle, entry);
  p.then((info) => {
    if (infoCache.get(handle) !== entry) return;
    if (info.failed) infoCache.delete(handle);
    else { entry.at = info.readAt; entry.tagged = rateFromTags(info.tags) > 0; }
  });
  return p;
}

async function readAll(handles, fresh, seen) {               // one read per product per check, POOL at a time
  const todo = handles.filter((h) => !seen.has(h));
  let i = 0;
  const worker = async () => {
    while (i < todo.length) {
      const h = todo[i++];
      const p = productInfo(h, fresh);
      seen.set(h, p);
      await p;
    }
  };
  await Promise.all(Array.from({ length: Math.min(POOL, todo.length) }, worker));
  const out = new Map();
  for (const h of handles) out.set(h, await seen.get(h));
  return out;
}

const feeCents = (unitCents, rate) => Math.floor((unitCents * rate + 50) / 100);
const isFeeLine = (it) => !!CFG && (Number(it.product_id) === CFG.product_id || (!!CFG.handle && it.handle === CFG.handle));
const labelFor = (it) => (it.product_title + (it.variant_title && it.variant_title !== 'Default Title' ? ' – ' + it.variant_title : '')).slice(0, 250);
const nonFeeCount = (cart) => cart.items.reduce((n, it) => n + (isFeeLine(it) ? 0 : it.quantity), 0);
const sameCart = (a, b) => !!a && !!b && a.items.length === b.items.length &&
  a.items.every((x, k) => x.key === b.items[k].key && x.quantity === b.items[k].quantity);

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
  return Array.from(ids);
}

function ladder(cents) {
  const parts = [];
  let left = cents;
  for (const d of LADDER) {
    if (!CFG.amounts[String(d)]) continue;
    const n = Math.floor(left / d);
    if (n > 0) { parts.push([d, n]); left -= n * d; }
  }
  return left === 0 && parts.length ? parts : null;
}

/* ---- the plan: what the cart's surcharge lines should be ------------------------------------------------------- */
async function buildPlan(cart, fresh, seen) {
  const ops = { updates: {}, adds: [] };
  const want = new Map();                                    // variant id -> { fee, qty, avail, rate, label }
  const failed = [];
  const unknown = new Set();                                 // items that could not be read: their lines are left alone
  const items = cart.items.filter((it) => !isFeeLine(it) && !it.gift_card);
  const infos = await readAll(Array.from(new Set(items.map((it) => it.handle))), fresh, seen);
  for (const it of items) {
    const info = infos.get(it.handle);
    const vid = Number(it.variant_id);
    if (!info || info.failed) { failed.push(it.product_title); unknown.add(vid); continue; }
    const rate = rateFromTags(info.tags);
    if (!(rate > 0)) continue;                               // untagged (0) or the surcharge product (null)
    if (!info.onhand || !(String(vid) in info.onhand)) {     // no count for this option: a failed lookup, not "0"
      failed.push(it.product_title);
      unknown.add(vid);
      continue;
    }
    const fee = feeCents(it.price, rate);
    if (fee <= 0) continue;
    const e = want.get(vid) || { fee, qty: 0, avail: info.onhand[String(vid)], rate, label: labelFor(it) };
    e.qty += it.quantity;
    want.set(vid, e);
  }
  for (const [vid, e] of want) {
    e.qty = Math.max(0, e.qty - e.avail);                     // only the units beyond the shelf pay
    if (e.qty === 0) want.delete(vid);
  }
  const have = new Map();
  for (const f of cart.items) {
    if (!isFeeLine(f)) continue;
    const forId = f.properties && f.properties[PROP_FOR] != null ? Number(f.properties[PROP_FOR]) : 0;
    if (forId && unknown.has(forId)) continue;               // its item could not be read: neither added nor taken
    if (!forId || !want.has(forId)) { ops.updates[f.key] = 0; continue; }  // stray, or its item needs none now
    const list = have.get(forId) || [];
    list.push(f);
    have.set(forId, list);
  }
  const unfixable = [];
  for (const [vid, e] of want) {
    const lines = have.get(vid) || [];
    const total = lines.reduce((s, f) => s + f.price * f.quantity, 0);
    if (lines.length === 1 && lines[0].price === e.fee && !(lines[0].properties || {})[PROP_PART]) {
      if (lines[0].quantity !== e.qty) ops.updates[lines[0].key] = e.qty;
      continue;
    }
    if (lines.length && total === e.fee * e.qty && lines.every((f) => (f.properties || {})[PROP_PART])) continue;
    for (const f of lines) ops.updates[f.key] = 0;
    const props = { [PROP_FOR]: String(vid), [PROP_RATE]: String(e.rate), [PROP_LABEL]: e.label };
    const exact = CFG.amounts[String(e.fee)];
    if (exact) {
      ops.adds.push({ id: Number(exact), quantity: e.qty, properties: props });
      continue;
    }
    const parts = ladder(e.fee);
    if (!parts) { unfixable.push({ variant: vid, fee: e.fee }); warn('no surcharge variant for', e.fee, 'cents'); continue; }
    for (const [cents, n] of parts) {
      ops.adds.push({ id: Number(CFG.amounts[String(cents)]), quantity: n * e.qty, properties: { ...props, [PROP_PART]: String(cents) } });
    }
  }
  return { ops, failed, unfixable, changes: Object.keys(ops.updates).length + ops.adds.length };
}

/* Horizon's own cart controls address lines by POSITION; while surcharge lines are added or removed the positions
   move, so the controls are locked for that moment (Horizon's cart-items-disabled class, pointer-events: none) and
   unlocked once Horizon has redrawn. A newer lock cancels an older unlock. */
let unlockTimer = 0;
function lockCart(on) {
  clearTimeout(unlockTimer);
  document.querySelectorAll('cart-items-component').forEach((el) => el.classList.toggle('cart-items-disabled', on));
}
function unlockSoon() {
  clearTimeout(unlockTimer);
  unlockTimer = setTimeout(() => lockCart(false), 400);
}

async function applyPlan(ops) {
  const ids = sectionIds();
  const hasUpd = Object.keys(ops.updates).length > 0;
  const hasAdd = ops.adds.length > 0;
  let last = null;
  if (hasUpd) {
    log('update', ops.updates);
    last = await fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ updates: ops.updates, ...(hasAdd ? {} : { sections: ids.join(','), sections_url: location.pathname }) }),
    });
  }
  if (hasAdd) {
    log('add', ops.adds);
    last = await fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: ops.adds, sections: ids.join(','), sections_url: location.pathname }),
    });
  }
  return last && last.sections ? last.sections : null;
}

/* Tell Horizon the cart changed, with the sections it should redraw (the same event its own cart uses). */
function announce(cart, sections) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const ev = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
  ev.tyeeTariff = true;
  document.dispatchEvent(ev);
  const count = nonFeeCount(cart);
  const base = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
  deferred.resolve({ cart: { ...base, totalQuantity: count }, detail: { sections: sections || undefined, items: cart.items, itemCount: count, source: 'tyee-tariff', didError: false } });
}

/* The header's count and label and Horizon's bubble count the surcharge's units too; say the item count instead.
   Horizon redraws a moment after it is told and prints its own count again, so for 3 s every redraw is corrected,
   always from the newest cart. */
let countObserver = null;
let countStop = 0;
function applyCounts() {
  const cart = state.countCart;
  if (!cart) return;
  const n = nonFeeCount(cart);
  document.querySelectorAll('.tm-cart-count, [ref="cartBubbleCount"], [ref="cartItemCount"]').forEach((el) => {
    if (el.textContent.trim() !== String(n)) el.textContent = String(n);
  });
  document.querySelectorAll('.tm-cart-count').forEach((el) => { el.hidden = n === 0; });
  document.querySelectorAll('a[aria-label^="Cart,"]').forEach((a) => {
    const want = 'Cart, ' + n + ' item' + (n === 1 ? '' : 's');
    if (a.getAttribute('aria-label') !== want) a.setAttribute('aria-label', want);
  });
}
function fixCounts(cart) {
  state.countCart = cart;
  applyCounts();
  if (!countObserver && typeof MutationObserver === 'function') {
    countObserver = new MutationObserver(() => applyCounts());
    countObserver.observe(document.body || document.documentElement, { childList: true, subtree: true, characterData: true });
  }
  clearTimeout(countStop);
  countStop = setTimeout(() => { if (countObserver) { countObserver.disconnect(); countObserver = null; } }, 3000);
}

function reconcile(reason) {
  if (!CFG || !CFG.enabled) return Promise.resolve(false);
  if (state.running) { state.queued = true; return state.running; }
  state.running = (async () => {
    const seen = new Map();                                  // one read per product for the whole run
    const fresh = reason === 'checkout';
    let changed = false;
    let settled = false;
    let sections = null;
    let cart = null;
    let plan = null;
    try {
      for (let pass = 0; pass < 4; pass++) {
        cart = await readCart();
        plan = await buildPlan(cart, fresh, seen);
        log('reconcile(' + reason + ') pass ' + pass + ': ' + plan.changes + ' change(s)');
        if (plan.changes === 0) { settled = true; break; }
        changed = true;
        lockCart(true);
        sections = await applyPlan(plan.ops);
      }
      if (!settled) {                                        // one last look: the last pass's writes may have done it
        cart = await readCart();
        plan = await buildPlan(cart, fresh, seen);
        settled = plan.changes === 0;
      }
      state.failed = plan ? plan.failed : [];
      state.unfixable = (plan ? plan.unfixable : []).concat(settled ? [] : [{ why: 'the cart did not settle' }]);
      state.settledCart = settled ? cart : null;             // the cart that was CHECKED - the one checkout must match
      if (changed) {
        // settled: the cart now is the one the last write left, and that write's sections are its drawing
        const shown = settled ? cart : await readCart();
        announce(shown, settled ? sections : null);
        fixCounts(shown);
      } else if (cart) {
        fixCounts(cart);
      }
    } finally {
      if (changed) unlockSoon();
    }
    if (!state.failed.length && !state.unfixable.length) state.lastOkAt = Date.now();
    return changed;
  })()
    .catch((e) => {
      warn('reconcile failed', e);
      lockCart(false);
      state.failed = ['(the cart could not be read)'];
      state.settledCart = null;
      return false;
    })
    .finally(() => {
      state.running = null;
      if (state.queued) { state.queued = false; reconcile('queued'); }
    });
  return state.running;
}

let debounce = 0;
function schedule(reason, delay = 60) {
  clearTimeout(debounce);
  state.scheduled = true;
  debounce = setTimeout(() => { state.scheduled = false; reconcile(reason); }, delay);
}

/* SWITCHED OFF (enabled:false, or no usable config) with surcharge lines still in a cart from while it was on: those
   lines are taken out, once, on the next page that shows the cart - the cart marks them (tyee-tariff-row) and hides
   their own remove button, so nobody else could. Locked while the lines move, like the on path. */
async function clearWhenOff() {
  const keys = Array.from(document.querySelectorAll('tr.tyee-tariff-row[data-key]')).map((tr) => tr.dataset.key);
  if (!keys.length) return;
  lockCart(true);
  try {
    const updates = Object.fromEntries(keys.map((k) => [k, 0]));
    const ids = sectionIds();
    const r = await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates, sections: ids.join(','), sections_url: location.pathname }) });
    announce(r && Array.isArray(r.items) ? r : await readCart(), r && r.sections ? r.sections : null);
    log('switched off: took out ' + keys.length + ' surcharge line(s)');
  } catch (e) {
    warn('could not take the surcharge lines out', e);
  } finally {
    unlockSoon();
  }
}

/* ---- the product page's panel: redrawn from the page Horizon's variant picker fetches -------------------------- */
/* Only inside the section whose picker changed (every product's block has the same id). */
function swapPanels(html, scope) {
  let doc = html;
  if (typeof html === 'string') doc = new DOMParser().parseFromString(html, 'text/html');
  if (!doc || typeof doc.querySelector !== 'function') return;
  scope.querySelectorAll('[data-tyee-tariff-block]').forEach((el) => {
    const fresh = doc.querySelector('[data-tyee-tariff-block="' + el.dataset.tyeeTariffBlock + '"]');
    el.innerHTML = fresh ? fresh.innerHTML : '';
  });
}
document.addEventListener('shopify:product:select', (e) => {
  const p = e.promise;
  if (!p || typeof p.then !== 'function') return;
  const t = e.target instanceof Element ? e.target : null;
  const scope = (t && t.closest('.shopify-section')) || document;
  p.then((r) => swapPanels(r && r.detail ? r.detail.html : null, scope), () => {});
});

/* ---- wiring, only when the surcharge is on ---------------------------------------------------------------------- */
if (CFG && CFG.enabled) {
  const HOLD = 'We could not work out the Canadian counter-tariff surcharge for an item in your cart. Please refresh the page and try again, or contact us and we will complete the order for you.';
  const WAIT = 'We are still checking your cart. Please try again in a moment.';
  const CHANGED = 'Your cart has just changed. Please check it and press Check out again.';

  const setBusy = (on) => {
    state.busy = on;
    document.querySelectorAll('#checkout, [name="checkout"], .cart__checkout-button').forEach((b) => {
      if (on) {
        b.setAttribute('aria-busy', 'true');
        b.dataset.tyeeWasDisabled = b.disabled ? '1' : '';
        b.disabled = true;
      } else {
        b.removeAttribute('aria-busy');
        if (!b.dataset.tyeeWasDisabled) b.disabled = false;
      }
    });
  };

  document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
    if (event.tyeeTariff) return;
    const p = event.promise;
    if (p && typeof p.then === 'function') p.then(() => schedule('event'), () => schedule('event-error'));
    else schedule('event');
  });

  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
    const h = init && init.headers;
    const mine = h && (h[MARK] || (typeof h.get === 'function' && h.get(MARK)));
    const res = origFetch.apply(this, arguments);
    if (!mine && CART_URL.test(url)) res.then(() => schedule('fetch', 150), () => {});
    return res;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_URL.test(String(url))) this.addEventListener('loadend', () => schedule('xhr', 150));
    return origOpen.apply(this, arguments);
  };

  const onLoad = () => reconcile('load');
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onLoad);
  else onLoad();
  window.addEventListener('pageshow', (e) => { if (e.persisted) reconcile('pageshow'); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - state.lastOkAt > 30000) reconcile('visible');
  });

  /* CHECKOUT. Wait for anything already running or queued, then check once more with fresh shelf counts. Then, and
     only if (1) a fresh read of the cart is EXACTLY the cart that check settled on and (2) the boxes on screen say the
     same, the browser goes to /checkout by itself - the cart form is never posted (it posts every row's quantity box
     by position). Anything else: the cart is redrawn and the customer asked to look and press again. Not finished in
     10 s, a lookup failed, or the cart did not settle: checkout waits, with a message. */
  async function check() {
    for (let i = 0; i < 200 && (state.running || state.queued || state.scheduled); i++) {
      if (state.running) await state.running;
      else await sleep(50);
    }
    await reconcile('checkout');
  }
  function screenMatches(cart) {
    const rows = Array.from(document.querySelectorAll('cart-items-component tr[data-key]'));
    if (!rows.length) return true;                          // a checkout link away from the cart: nothing on screen
    const byKey = new Map(cart.items.map((i) => [i.key, i.quantity]));
    for (const tr of rows) {
      if (tr.classList.contains('tyee-tariff-row')) continue;
      const box = tr.querySelector('input[name="updates[]"]');
      if (box && byKey.get(tr.dataset.key) !== Number(box.value)) return false;
    }
    const shown = new Set(rows.map((tr) => tr.dataset.key));
    return cart.items.every((i) => isFeeLine(i) || shown.has(i.key));
  }
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  async function toCheckout(href) {
    if (state.busy) return;
    setBusy(true);
    let done = false;
    let fresh = null;
    try {
      await Promise.race([check().then(() => { done = true; }), sleep(CHECK_CAP)]);
      if (done) fresh = await readCart();
    } catch (e) {
      warn('checkout check failed', e);
    } finally {
      setBusy(false);
    }
    if (!done) { alert(WAIT); return; }
    if (state.failed.length || state.unfixable.length) { alert(HOLD); return; }
    const checked = state.settledCart;
    if (!checked || !sameCart(fresh, checked) || !screenMatches(checked)) {
      if (fresh) announce(fresh, null);
      schedule('changed', 0);
      alert(CHANGED);
      return;
    }
    window.location.href = href || root() + 'checkout';
  }
  window.TyeeTariff.toCheckout = toCheckout;

  const isCheckoutButton = (el) => el instanceof Element && !!el.closest('button[name="checkout"], #checkout, .cart__checkout-button');
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    if (isCheckoutButton(t)) {                               // caught on the click, so a browser without e.submitter is caught too
      e.preventDefault();
      e.stopImmediatePropagation();
      toCheckout();
      return;
    }
    const a = t.closest('a[href]');
    if (!a || !/^(https?:\/\/[^/]+)?\/checkout(\?|$)/.test(a.getAttribute('href') || '')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    toCheckout(a.href);                                      // a link keeps its own address (a discount code, say)
  }, true);
  document.addEventListener('submit', (e) => {               // Enter in a quantity box submits the cart form
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const goes = form.id === 'cart-form' || (e.submitter && e.submitter.name === 'checkout') || /\/checkout(\?|$)/.test(form.getAttribute('action') || '');
    if (!goes) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    toCheckout();
  }, true);

  // a press made before this script had started (held by snippets/tyee-tariff.liquid) is done now
  window.__tyeeTariffReady = true;
  if (window.__tyeeTariffPending) {
    const href = window.__tyeeTariffPending.href;
    window.__tyeeTariffPending = null;
    clearTimeout(window.__tyeeTariffRelease);
    toCheckout(href);
  }
} else {
  window.__tyeeTariffReady = true;
  const go = () => clearWhenOff();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
  else go();
}
